import { useEffect, useState } from "react"
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom"
import { ArrowRight, Eye, EyeOff, Lock, Mail, PawPrint } from "lucide-react"
import { signup } from "~/api/auth"
import { getAssessmentSession } from "~/api/assessmentSessions"
import type { AssessmentSessionResponse, AskDynamoQuestion } from "~/api/assessmentSessions"
import { AskDynamoSignupCallout } from "~/components/AskDynamoSignupCallout"
import { Logo } from "~/components/Logo"
import { PageShell } from "~/components/PageShell"
import { SignupBenefitsList } from "~/components/SignupBenefitsList"
import { SignupHero } from "~/components/SignupHero"
import { PhotoUploadPanel } from "~/components/PhotoUploadPanel"
import { trackEvent } from "~/utils/trackEvent"

type SignupLocationState = { assessmentSessionToken?: string; dogName?: string } | null

function pickHighlightQuestion(questions: AskDynamoQuestion[]): string {
  return questions.find((q) => q.icon === "heart")?.text ?? questions[0]?.text ?? ""
}

export function SignupPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const state = location.state as SignupLocationState
  const assessmentSessionToken = state?.assessmentSessionToken ?? searchParams.get("token")
  const stateDogName = state?.dogName?.trim() || null

  const [session, setSession] = useState<AssessmentSessionResponse | null>(null)
  const [sessionLoading, setSessionLoading] = useState(!!assessmentSessionToken)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [portraitPhoto, setPortraitPhoto] = useState<File | null>(null)
  const [portraitPreviewUrl, setPortraitPreviewUrl] = useState<string | null>(null)
  const [photoError, setPhotoError] = useState<string | null>(null)

  useEffect(() => {
    if (!assessmentSessionToken) {
      setSessionLoading(false)
      return
    }
    getAssessmentSession(assessmentSessionToken)
      .then(setSession)
      .catch(() => setSession(null))
      .finally(() => setSessionLoading(false))
  }, [assessmentSessionToken])

  const dogName = session?.dog_name?.trim() || stateDogName || "your dog"
  const archetype = session?.archetype
  const showHero = !!archetype?.icon && !!archetype?.name
  const highlightQuestion =
    archetype?.ask_dynamo?.example_questions &&
    pickHighlightQuestion(archetype.ask_dynamo.example_questions)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (submitting) return
    setError(null)
    setPhotoError(null)
    if (!email.trim() || !password) return
    setSubmitting(true)
    try {
      const data = await signup(email.trim(), password, assessmentSessionToken, portraitPhoto)
      if (!data.user.email_verified) {
        navigate("/verify-email", { replace: true })
      } else {
        navigate("/dashboard", { replace: true })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign up failed")
    } finally {
      setSubmitting(false)
    }
  }

  function handleSubmitClick() {
    trackEvent("signup_cta_tap", { dogName })
  }

  if (sessionLoading) {
    return (
      <PageShell innerClassName="max-w-lg w-full">
        <Logo size="large" />
        <p className="text-sm text-[var(--color-text-secondary)]">Loading...</p>
      </PageShell>
    )
  }

  return (
    <PageShell innerClassName="max-w-lg w-full">
      <Logo size="large" />

      <div className="flex w-full flex-col gap-6 text-center">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">
            Unlock {dogName}&apos;s full personality
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-[var(--color-text-secondary)]">
            Create your free account to continue your journey and get personalised guidance tailored
            to {dogName}.
          </p>
        </div>

        {showHero && (
          <SignupHero
            dogName={dogName}
            archetypeName={archetype!.name}
            portraitIcon={archetype!.icon}
          />
        )}

        <SignupBenefitsList dogName={dogName} />

        {highlightQuestion && (
          <AskDynamoSignupCallout dogName={dogName} exampleQuestion={highlightQuestion} />
        )}

        <form onSubmit={handleSubmit} className="flex w-full flex-col gap-4 text-left">
          <PhotoUploadPanel
            dogName={dogName}
            photo={portraitPhoto}
            previewUrl={portraitPreviewUrl}
            error={photoError}
            onPhotoSelected={(file, preview) => {
              setPortraitPhoto(file)
              setPortraitPreviewUrl(preview)
              setPhotoError(null)
            }}
            onClear={() => {
              setPortraitPhoto(null)
              setPortraitPreviewUrl(null)
            }}
          />

          <div>
            <label htmlFor="signup-email" className="block text-sm font-medium text-[var(--color-text-primary)]">
              Email
            </label>
            <div className="relative mt-1">
              <Mail
                size={18}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-secondary)]"
                aria-hidden
              />
              <input
                id="signup-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
                className="w-full rounded-lg border border-[var(--color-divider)] bg-[var(--color-bg-primary)] py-3 pl-10 pr-4 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-secondary)]/70 focus:border-[var(--color-divider)] focus:outline-none focus:ring-1 focus:ring-[var(--color-divider)]"
              />
            </div>
          </div>

          <div>
            <label htmlFor="signup-password" className="block text-sm font-medium text-[var(--color-text-primary)]">
              Password
            </label>
            <div className="relative mt-1">
              <Lock
                size={18}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-secondary)]"
                aria-hidden
              />
              <input
                id="signup-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                placeholder="Create a password"
                className="w-full rounded-lg border border-[var(--color-divider)] bg-[var(--color-bg-primary)] py-3 pl-10 pr-11 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-secondary)]/70 focus:border-[var(--color-divider)] focus:outline-none focus:ring-1 focus:ring-[var(--color-divider)]"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-text-secondary)] transition hover:text-[var(--color-text-primary)]"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <p className="text-sm text-[var(--color-text-secondary)]">
            By creating an account you agree to our{" "}
            <Link to="/privacy-policy" className="underline">
              Privacy Policy
            </Link>{" "}
            and{" "}
            <Link to="/terms" className="underline">
              Terms of Service
            </Link>
            .
          </p>

          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            onClick={handleSubmitClick}
            disabled={submitting}
            aria-busy={submitting}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--color-ask-dynamo)] px-6 py-3 font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <PawPrint size={18} aria-hidden />
            {submitting ? "Creating account…" : "Create Free Account"}
            {!submitting && <ArrowRight size={18} aria-hidden />}
          </button>
        </form>

        <p className="text-sm text-[var(--color-text-secondary)]">
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-[var(--color-ask-dynamo)] underline">
            Log in
          </Link>
        </p>
      </div>
    </PageShell>
  )
}
